import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["repliesContainer", "toggleButton", "replyForm", "replyCount"]
  
  connect() {
    console.log('💬 Comment Replies Controller connected')
    this.repliesVisible = false
    this.formVisible = false
    this.isTransitioning = false
    
    // Listen for exit thread events
    this.element.addEventListener('exitThread', () => this.exitThreadMode())
  }
  
  toggleReplies(event) {
    event.preventDefault()
    
    // Prevent double-clicks and rapid toggling
    if (this.isTransitioning) {
      console.log('⚠️ Transition in progress, ignoring click')
      return
    }
    
    if (!this.hasRepliesContainerTarget) return
    
    this.repliesVisible = !this.repliesVisible
    
    if (this.repliesVisible) {
      this.enterThreadMode()
      // Make the toggle button non-clickable after opening replies
      this.disableToggleButton()
    } else {
      this.exitThreadMode()
    }
  }
  
  // Disable the toggle button to prevent re-clicking
  disableToggleButton() {
    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.disabled = true
      this.toggleButtonTarget.classList.remove('hover:text-blue-600', 'dark:hover:text-night-primary', 'cursor-pointer')
      this.toggleButtonTarget.classList.add('cursor-default', 'opacity-60')
      // Remove the click handler
      this.toggleButtonTarget.removeAttribute('data-action')
    }
  }
  
  toggleReplyForm(event) {
    event.preventDefault()
    
    if (!this.hasReplyFormTarget) return
    
    this.formVisible = !this.formVisible
    
    if (this.formVisible) {
      this.replyFormTarget.classList.remove('hidden')
      // Focus the textarea
      const textarea = this.replyFormTarget.querySelector('textarea')
      if (textarea) {
        textarea.focus()
      }
    } else {
      this.replyFormTarget.classList.add('hidden')
    }
  }
  
  showReplyForm(event) {
    event.preventDefault()
    
    // First ensure replies are visible
    if (!this.repliesVisible && this.hasRepliesContainerTarget) {
      this.repliesVisible = true
      this.repliesContainerTarget.classList.remove('hidden')
    }
    
    // Then show the form
    if (this.hasReplyFormTarget) {
      this.formVisible = true
      this.replyFormTarget.classList.remove('hidden')
      const textarea = this.replyFormTarget.querySelector('textarea')
      if (textarea) {
        setTimeout(() => textarea.focus(), 100)
      }
    }
  }
  
  cancelReply(event) {
    event.preventDefault()
    
    if (this.hasReplyFormTarget) {
      this.formVisible = false
      this.replyFormTarget.classList.add('hidden')
      
      // Clear the form
      const textarea = this.replyFormTarget.querySelector('textarea')
      if (textarea) {
        textarea.value = ''
      }
    }
  }
  
  handleReplySubmit(event) {
    // This will be called after successful form submission
    console.log('Reply submitted successfully')
    
    // Stop event from bubbling up to prevent inline-comments controller from interfering
    event.stopPropagation()
    
    // Ensure replies container stays visible to show the new reply
    if (this.hasRepliesContainerTarget) {
      this.repliesVisible = true
      this.repliesContainerTarget.classList.remove('hidden')
      this.repliesContainerTarget.dataset.loaded = 'true'
    }
    
    // Clear the form that was submitted
    const form = event.target
    if (form && form.tagName === 'FORM') {
      const textarea = form.querySelector('textarea[name="comment[body]"]')
      if (textarea) {
        textarea.value = ''
        // Reset height to minimum
        const minHeight = parseInt(textarea.style.minHeight) || 50
        textarea.style.height = minHeight + 'px'
        // Don't refocus - let the user stay where they are
      }
    }
  }
  
  loadReplies() {
    // Mark as loaded to prevent duplicate requests
    this.repliesContainerTarget.dataset.loaded = 'true'
    
    // The replies are already loaded via includes in the controller
    // This is a placeholder for dynamic loading if needed
    console.log('Replies already loaded via server-side includes')
  }
  
  enterThreadMode() {
    // Prevent entering thread mode if already transitioning or if originalContent exists
    if (this.isTransitioning || this.originalContent) {
      console.log('⚠️ Already in thread mode or transitioning')
      return
    }
    
    // Find the commentable container's parent (includes comment form)
    const commentableParent = document.querySelector('[data-controller="comments-stream"]')?.parentElement
    if (!commentableParent) return
    
    // Set transitioning flag
    this.isTransitioning = true
    
    // Store original content only if not already stored
    if (!this.originalContent) {
      this.originalContent = commentableParent.innerHTML
    }
    
    // Create thread view content
    const threadContent = this.createThreadView()
    
    // Replace the entire content (including comment form)
    commentableParent.innerHTML = threadContent
    
    // Remove old back button override - let links work naturally
    
    // Focus the reply textarea
    const textarea = commentableParent.querySelector('textarea')
    if (textarea) {
      setTimeout(() => textarea.focus(), 100)
    }
    
    // Clear transitioning flag after a delay
    setTimeout(() => {
      this.isTransitioning = false
    }, 300)
  }
  
  createThreadView() {
    const currentComment = this.element
    const commentableElement = document.querySelector('[data-controller="comments-stream"]')
    
    // Clone the comment and its replies
    const commentClone = currentComment.cloneNode(true)
    const repliesContainer = currentComment.querySelector('[data-comment-replies-target="repliesContainer"]')
    
    // Show replies in the clone
    if (repliesContainer) {
      const repliesClone = repliesContainer.cloneNode(true)
      repliesClone.classList.remove('hidden')
      if (repliesClone.querySelector('[data-comment-replies-target="replyForm"]')) {
        repliesClone.querySelector('[data-comment-replies-target="replyForm"]').classList.remove('hidden')
      }
      commentClone.querySelector('[data-comment-replies-target="repliesContainer"]').replaceWith(repliesClone)
    }
    
    // Preserve the turbo stream connection by keeping the comments-stream controller
    return `
      <div class=""
           data-controller="comments-stream"
           data-comments-stream-commentable-type-value="${commentableElement.dataset.commentsStreamCommentableTypeValue}"
           data-comments-stream-commentable-id-value="${commentableElement.dataset.commentsStreamCommentableIdValue}">
        
        <!-- Turbo Stream connection for real-time updates -->
        ${commentableElement.querySelector('[data-turbo-stream-from]')?.outerHTML || ''}
        
        <!-- Thread Navigation -->
        <div class="bg-gray-50 dark:bg-night-surface border-b border-gray-200 dark:border-night-border p-3 flex items-center space-x-3">
          <button class="back-button flex items-center space-x-2 text-gray-600 dark:text-night-text-secondary hover:text-gray-900 dark:hover:text-night-text-primary">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
            <span class="text-sm font-medium">Back to all comments</span>
          </button>
        </div>
        
        <!-- Thread Content -->
        <div class="space-y-4">
          ${commentClone.outerHTML}
        </div>
      </div>
    `
  }
  
  exitThreadMode() {
    // Prevent exiting if transitioning
    if (this.isTransitioning) {
      console.log('⚠️ Transition in progress, cannot exit thread mode')
      return
    }
    
    // Find the parent container that we replaced
    const parentContainer = document.querySelector('[data-controller="comments-stream"]')?.parentElement || 
                           document.querySelector('.back-button')?.closest('div')?.parentElement
    if (!parentContainer || !this.originalContent) {
      console.log('⚠️ Cannot exit thread mode: missing parent container or original content')
      return
    }
    
    // Set transitioning flag
    this.isTransitioning = true
    
    // Restore original content
    parentContainer.innerHTML = this.originalContent
    
    // Reset state
    this.repliesVisible = false
    this.originalContent = null
    
    // Clear transitioning flag after a delay
    setTimeout(() => {
      this.isTransitioning = false
    }, 300)
  }
  
  updateReplyCount(newCount) {
    if (this.hasReplyCountTarget) {
      // Add animation class
      this.replyCountTarget.classList.add('count-updated')
      
      // Update the count
      this.replyCountTarget.textContent = newCount
      
      // Remove animation class after animation completes
      setTimeout(() => {
        this.replyCountTarget.classList.remove('count-updated')
      }, 600)
    }
  }
}